import { create } from "zustand";
import {
  buildWindows,
  loadDeviceHistory,
  processInBatches,
  toNumber,
  DEVICE_CONCURRENCY,
} from "../../pages/reports/utils/trackingHistoryWindows";
import {
  IDLE_CONFIG,
  odometerKmFromParams,
} from "../shared/vehicleStatusRule";

const DAY_MS = 24 * 60 * 60 * 1000;

export const SPEED_COLORS = [
  "#2563EB",
  "#DB2777",
  "#16A34A",
  "#D97706",
  "#7C3AED",
  "#0891B2",
  "#DC2626",
  "#65A30D",
];

export interface SpeedExceso {
  ts: number;
  lat: number;
  lon: number;
  speed: number;
  durationSeconds: number;
  distanceKm: number;
}

export interface SpeedSeriesPoint {
  ts: number;
  speed: number;
}

export interface SpeedResult {
  deviceId: string;
  vehicleName: string;
  color: string;
  excesos: SpeedExceso[];
  series: SpeedSeriesPoint[];
  totalExcesos: number;
  totalDistance: number;
  speedSum: number;
  maxSpeed: number;
  loading: boolean;
  error: string | null;
}

export interface SpeedDevice {
  deviceId: string;
  vehicleName: string;
}

interface ReportSpeedState {
  results: Record<string, SpeedResult>;
  order: string[];
  loading: boolean;
  from: string | null;
  to: string | null;
  tz: string | null;
  minSpeed: number;
}

interface ReportSpeedActions {
  loadRange: (
    devices: SpeedDevice[],
    from: string,
    to: string,
    opts: { minSpeed: number; tz?: string },
  ) => Promise<void>;
  clear: () => void;
}

type ReportSpeedStore = ReportSpeedState & ReportSpeedActions;

const haversineKm = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const useReportSpeedStore = create<ReportSpeedStore>()((set) => ({
  results: {},
  order: [],
  loading: false,
  from: null,
  to: null,
  tz: null,
  minSpeed: 0,

  loadRange: async (devices, from, to, opts) => {
    const resolvedTz = opts.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const minSpeed = opts.minSpeed || 0;
    const order = devices.map((d) => d.deviceId);

    const fromMs = new Date(from).getTime();
    const toMs = new Date(to).getTime();
    const windows = buildWindows(fromMs, toMs);

    const initial: Record<string, SpeedResult> = {};
    devices.forEach((d, i) => {
      initial[d.deviceId] = {
        deviceId: d.deviceId,
        vehicleName: d.vehicleName,
        color: SPEED_COLORS[i % SPEED_COLORS.length],
        excesos: [],
        series: [],
        totalExcesos: 0,
        totalDistance: 0,
        speedSum: 0,
        maxSpeed: 0,
        loading: true,
        error: null,
      };
    });

    set({ results: initial, order, loading: true, from, to, tz: resolvedTz, minSpeed });

    await processInBatches(devices, DEVICE_CONCURRENCY, async (d) => {
      const { points, failedChunks } = await loadDeviceHistory(
        d.deviceId,
        windows,
        resolvedTz,
      );

      const parsed = points
        .map((p) => ({
          ts: p.ts,
          speed: toNumber(p.speed),
          ignition: p.ignition === true,
          lat: toNumber(p.lat) ?? 0,
          lon: toNumber(p.lng) ?? 0,
          odoKm: odometerKmFromParams(p.params),
        }))
        .filter((p) => p.speed != null) as Array<{
        ts: number;
        speed: number;
        ignition: boolean;
        lat: number;
        lon: number;
        odoKm: number | null;
      }>;

      const series: SpeedSeriesPoint[] = parsed.map((p) => ({
        ts: p.ts,
        speed: p.speed,
      }));

      const excesos: SpeedExceso[] = [];
      let totalDistance = 0;
      let speedSum = 0;
      let maxSpeed = 0;

      for (let i = 0; i < parsed.length; i++) {
        const p = parsed[i];
        if (p.speed > maxSpeed) maxSpeed = p.speed;

        const isIdle = p.speed <= IDLE_CONFIG.movementThreshold && p.ignition;
        if (isIdle) continue;
        if (p.speed < minSpeed) continue;

        const next = i < parsed.length - 1 ? parsed[i + 1] : null;
        let durationSeconds = 0;
        let distanceKm = 0;
        if (next) {
          durationSeconds = (next.ts - p.ts) / 1000;
          if (p.odoKm != null && next.odoKm != null) {
            distanceKm = Math.abs(next.odoKm - p.odoKm);
          } else {
            distanceKm = haversineKm(p.lat, p.lon, next.lat, next.lon);
          }
        }

        totalDistance += distanceKm;
        speedSum += p.speed;
        excesos.push({
          ts: p.ts,
          lat: p.lat,
          lon: p.lon,
          speed: p.speed,
          durationSeconds,
          distanceKm,
        });
      }

      set((state) => {
        const current = state.results[d.deviceId];
        if (!current) return state;
        return {
          results: {
            ...state.results,
            [d.deviceId]: {
              ...current,
              excesos,
              series,
              totalExcesos: excesos.length,
              totalDistance,
              speedSum,
              maxSpeed,
              loading: false,
              error:
                parsed.length === 0 && failedChunks > 0
                  ? "No se pudo obtener la velocidad (servicio no disponible)."
                  : parsed.length === 0
                    ? "Sin datos de velocidad en el rango."
                    : failedChunks > 0
                      ? `${failedChunks} tramo(s) con error`
                      : null,
            },
          },
        };
      });
    });

    set({ loading: false });
  },

  clear: () =>
    set({
      results: {},
      order: [],
      loading: false,
      from: null,
      to: null,
      tz: null,
      minSpeed: 0,
    }),
}));

export const speedRangeDays = (from: string, to: string): number =>
  Math.ceil(Math.abs(new Date(to).getTime() - new Date(from).getTime()) / DAY_MS) || 1;
